import { GQL, Utils } from "@binale-tech/shared";
import { type KeyboardEvent } from "react";
import { ALL_DOCUMENTS, DmsTypeOptions, isGroupType } from "@dms/configs/constants";
import { AppConfigUtils } from "@dms/scripts/utils/AppConfigUtils";
import { DmsType, type IDocumentType, type ITableColumns } from "@domain/models/dms";
import {
    DmsDefaultSubType,
    type IInitColumnsType,
    type ITableDocument,
    SubTypeActions,
    type TFilterConfigItem,
} from "@dms/types";
import {
    documentSubTypeCreate,
    documentSubTypeDelete,
    documentSubTypeUpdate,
} from "@domain/context/mutations/documentMutations.graphql";
import { type ApolloClient } from "@apollo/client";
import type { Key } from "@heroui/react";

export enum AccountingExportDisabledReason {
    NotAccountingType = "not_accounting_type",
    TooManyDocuments = "too_many_documents",
    MixedTypes = "mixed_types",
    NoType = "no_type",
    DocumentsNotReady = "documents_not_ready",
    NoGroupId = "no_group_id",
    MissingDocumentDate = "missing_document_date",
    YearNotInAccountingYears = "year_not_in_accounting_years",
    NotAReceipt = "not_a_receipt",
}

export class DmsUtils {
    static getActiveTypeAndSubType = (activeType: string[]) => {
        const lastIndex = activeType.length - 1;
        const type = activeType[lastIndex] as DmsType | typeof ALL_DOCUMENTS;
        const subType: string | undefined = activeType[lastIndex - 1];
        return { type, subType };
    };

    /**
     * Folder keyPath → uploader target. Non-uploadable views (empty selection,
     * trash, the all-documents pseudo folder) fall back to Neue Dokumente so a
     * user can never file an upload into trash by accident.
     */
    static getUploadTargetType = (activeType: string[]): string[] => {
        const { type } = DmsUtils.getActiveTypeAndSubType(activeType);
        if (!type || type === DmsType.trash || type === ALL_DOCUMENTS) {
            return [DmsType.new_documents];
        }
        return activeType;
    };

    static getDownloadName = (document: GQL.IDocument) => {
        if (!document) {
            return undefined;
        }
        return document.externalReference ? document.externalReference + ".pdf" : document.fileName;
    };
    static getFilterConfig = (filterConfig: Map<string, TFilterConfigItem>, typeKey: string[]) => {
        return filterConfig.get(typeKey.toString());
    };

    static getGroupId = (type: string, subType: string, documentTypes: IDocumentType[]) => {
        if (type === GQL.IProductKey.Bank) {
            const subTypes = documentTypes.find((v) => v.id === DmsType.Bank)?.subTypes ?? [];
            return subTypes.find((v) => v.id === subType)?.bankId;
        }
        if (type === GQL.IProductKey.Kb) {
            const subTypes = documentTypes.find((v) => v.id === DmsType.KB)?.subTypes ?? [];
            return subTypes.find((v) => v.id === subType)?.kbId;
        }
        return undefined;
    };

    static isInformational = (doc: GQL.IDocument): boolean => Utils.DmsDocumentRules.isInformational(doc);

    static isAccountingExportDisabled = (
        documents: GQL.IDocument[],
        type: string,
        groupId?: string,
        accountingYears?: number[],
    ): AccountingExportDisabledReason | null => {
        // Missing documentKind behaves as INVOICE; a mixed selection stays exportable.
        if (documents.length > 0 && documents.every((doc) => DmsUtils.isInformational(doc))) {
            return AccountingExportDisabledReason.NotAReceipt;
        }
        if (!type) {
            return AccountingExportDisabledReason.NoType;
        }
        // Informational documents are skipped by the backend gate, so they never block the rest (Ruling 6).
        const bookable = documents.filter((doc) => !DmsUtils.isInformational(doc));
        if (bookable.some((doc) => !doc.documentDate)) {
            return AccountingExportDisabledReason.MissingDocumentDate;
        }
        const areDocumentsReady = bookable.every((doc) => this.getDocumentStatus(doc) === GQL.IDocumentStatus.Ready);
        if (!areDocumentsReady) {
            return AccountingExportDisabledReason.DocumentsNotReady;
        }
        if ([GQL.IProductKey.Bank, GQL.IProductKey.Kb].includes(type as GQL.IProductKey) && !groupId) {
            return AccountingExportDisabledReason.NoGroupId;
        }
        if (accountingYears?.length) {
            const yearsSet = new Set(accountingYears);
            if (bookable.some((doc) => !yearsSet.has(+doc.documentDate.slice(0, 4)))) {
                return AccountingExportDisabledReason.YearNotInAccountingYears;
            }
        }
        return null;
    };

    static countCalc(documentsArr: GQL.IDocument[]) {
        const totalCount: Map<string, number> = new Map();
        const draftCount: Map<string, number> = new Map();

        documentsArr.forEach((doc) => {
            const increment = (key: string) => {
                totalCount.set(key, (totalCount.get(key) ?? 0) + 1);
                if ([ALL_DOCUMENTS, DmsType.trash, DmsType.new_documents].includes(doc.type)) {
                    // we don't use draft counter for this folders
                    return;
                }
                if (this.getDocumentStatus(doc) !== GQL.IDocumentStatus.Ready) {
                    draftCount.set(key, (draftCount.get(key) ?? 0) + 1);
                }
            };

            increment(doc.type);

            if (!isGroupType(doc.type as DmsType)) {
                return;
            }

            increment(AppConfigUtils.getDefaultSubTypeItemKey(doc.type, DmsDefaultSubType.all_subTypes));

            if (doc.subType) {
                increment([doc.subType, doc.type].toString());
            } else {
                increment(AppConfigUtils.getDefaultSubTypeItemKey(doc.type, DmsDefaultSubType.no_subTypes));
            }
        });
        return { draftCount, totalCount };
    }

    static getDocumentCount = (typeKey: string[], countMap?: Map<string, number>): number | undefined => {
        if (!countMap) {
            return undefined;
        }

        return countMap.get(typeKey.toString());
    };

    static menuSubTypeAction = async (arg: {
        typeKeyPath: string[];
        action: SubTypeActions;
        companyId: string;
        client: ApolloClient;
        fetchTypes: () => void;
        setFilterConfig: (typeKey: string[], configData: TFilterConfigItem) => void;
        setTableEnabledColumns: (typeKey: string[], enabledColumns: (keyof ITableColumns)[]) => void;
        deleteTypeConfig: (typeKey: string[]) => void;
        value?: string;
    }) => {
        const {
            typeKeyPath,
            action,
            companyId,
            client,
            fetchTypes,
            setTableEnabledColumns,
            setFilterConfig,
            deleteTypeConfig,
            value,
        } = arg;
        const { type, subType } = this.getActiveTypeAndSubType(typeKeyPath);

        switch (action) {
            case SubTypeActions.createSubType:
                if (value) {
                    const input: GQL.IDocumentSubTypeCreateInput = {
                        name: value,
                        companyId,
                        dmsDocumentTypeId: type,
                        icon: undefined,
                    };

                    const res = await client.mutate({
                        mutation: documentSubTypeCreate,
                        variables: { input },
                    });

                    const subTypeCreate = res.data?.documentSubTypeCreate;

                    if (subTypeCreate) {
                        const { defaultColumnsConfig, defaultFilterConfig } = AppConfigUtils.getDefaultConfig(
                            type as keyof IInitColumnsType,
                        );

                        setTableEnabledColumns([subTypeCreate.id, ...typeKeyPath], defaultColumnsConfig);
                        setFilterConfig([subTypeCreate.id, ...typeKeyPath], defaultFilterConfig);
                    }
                }

                break;
            case SubTypeActions.updateSubType:
                if (typeKeyPath && value) {
                    const input: GQL.IDocumentSubTypeUpdateInput = {
                        companyId,
                        dmsDocumentTypeId: type,
                        id: subType,
                        name: value,
                    };

                    await client.mutate({
                        mutation: documentSubTypeUpdate,
                        variables: { input },
                    });
                }
                break;
            case SubTypeActions.deleteSubType:
                if (typeKeyPath) {
                    const input: GQL.IDocumentSubTypeDeleteInput = {
                        companyId,
                        dmsDocumentTypeId: type,
                        id: subType,
                    };

                    await client.mutate({
                        mutation: documentSubTypeDelete,
                        variables: { input },
                    });

                    deleteTypeConfig(typeKeyPath);
                }
                break;
        }

        fetchTypes();
    };

    static getTypeAndSubType = (
        value: string,
        documentTypes?: IDocumentType[],
    ): { type?: string; subType?: string } => {
        if (!documentTypes) {
            return {};
        }

        const isType = DmsTypeOptions.find((t) => t.value === value);

        if (isType) {
            return { type: value };
        }

        let type: string | undefined;
        const subType = value;

        documentTypes.forEach((documentType) => {
            if (documentType.subTypes) {
                documentType.subTypes?.forEach((subtype) => {
                    if (subtype.id === subType) {
                        type = subtype.dmsDocumentTypeId;
                    }
                });
            }
        });

        return {
            type,
            subType,
        };
    };

    static isSubtypeHasDocument = (typeKeyPath: string[], documents: GQL.IDocument[]) => {
        const { subType } = this.getActiveTypeAndSubType(typeKeyPath);
        return documents.findIndex((el) => el.subType === subType) >= 0;
    };

    static formFocus = (
        e: KeyboardEvent<HTMLFormElement>,
        keyboardKeys: Set<string>,
        keyType: "up" | "down",
        parentId: string,
    ) => {
        if (e.key !== "Enter") {
            return;
        }

        e.preventDefault();

        if (keyType === "down") {
            keyboardKeys.add(e.key);
        } else if (keyType === "up") {
            keyboardKeys.delete(e.key);
        }

        const activeEl = document.activeElement;

        if (!activeEl) {
            return;
        }

        const formEl = document.getElementById(parentId);

        const focusableElements = `button:not([tabIndex="-1"]), input:not([tabIndex="-1"]), textarea:not([tabIndex="-1"])`;
        const inputsArr: HTMLInputElement[] = Array.from(formEl?.querySelectorAll(focusableElements) || []);
        const filteredArr = inputsArr.filter((el: HTMLInputElement) => !el.disabled && el.tabIndex !== -1);

        const index = filteredArr.indexOf(activeEl as HTMLInputElement);
        if (index === -1) {
            return;
        }

        if (keyboardKeys.has("Shift") && keyboardKeys.has("Enter")) {
            const prevIndex = index - 1;
            if (prevIndex >= 0) {
                const prevEl: HTMLInputElement = filteredArr[prevIndex];
                if (prevEl) {
                    prevEl.focus();
                }
            }
            return;
        }

        if (!keyboardKeys.has("Shift") && keyboardKeys.has("Enter")) {
            const nextIndex = index + 1;
            if (nextIndex < filteredArr.length) {
                const nextEl: HTMLInputElement = filteredArr[nextIndex];
                if (nextEl) {
                    nextEl.focus();
                }
            }
        }
    };

    static sortSelectedRowsFirst = (data: ITableDocument[], selectedRowKeys: Key[]) => {
        return data.slice().toSorted((a, b) => {
            const aSelected = selectedRowKeys.includes(a.key);
            const bSelected = selectedRowKeys.includes(b.key);
            if (aSelected && !bSelected) {
                return -1;
            }
            if (!aSelected && bSelected) {
                return 1;
            }
            return 0;
        });
    };

    // The rule lives in shared so the server-side find filter applies the same one.
    static getDocumentStatus = (document: GQL.IDocument): GQL.IDocumentStatus =>
        Utils.DmsDocumentRules.getDocumentStatus(document);
}
